import React, { FC, useContext, useEffect, useState } from 'react';
import { useShopConfigV2 } from '../../hooks/shopConfig';
import AppContext from '../../context/AppContext';
import BackgroundRemovalModalBase from './BackgroundRemovalModalBase';
import {
  BackgroundRemovalErrorResponseCode,
  handleRemoveBackground,
} from './backgroundRemovalUtils';
import { handle } from '../../util/helpers';
import { useOwnListingVersions } from '../../containers/EditListingPage/hooks/useOwnListingVersions';

interface CoverPhotoBackgroundRemovalModalProps {
  originalImageSrc: string;
  open: boolean;
  onClose: () => void;
  onCoverPhotoBackgroundRemovalModalSubmit: (base64ImageToUpload?: string) => void;
  isUploading: boolean;
  hasUploadError: boolean;
}

const CoverPhotoBackgroundRemovalModal: FC<CoverPhotoBackgroundRemovalModalProps> = (
  props: CoverPhotoBackgroundRemovalModalProps
) => {
  const {
    originalImageSrc,
    open,
    onClose,
    onCoverPhotoBackgroundRemovalModalSubmit,
    isUploading,
    hasUploadError,
  } = props;

  const [isRemovingBackground, setIsRemovingBackground] = useState(false);
  const [removeBackgroundError, setRemoveBackgroundError] = useState<
    string | BackgroundRemovalErrorResponseCode | null
  >(null);
  const [tempBGRemovedImageBase64, setTempBGRemovedImageBase64] = useState('');
  const { shopName } = useShopConfigV2();
  const { currentListing } = useOwnListingVersions();
  const { treetId } = useContext(AppContext);

  useEffect(() => {
    const removeBackground = async () => {
      const [tempBase64, error] = await handle(
        handleRemoveBackground({
          imageUrl: originalImageSrc,
          currentListingUuid: currentListing?.id?.uuid,
          treetId,
          shopName,
        })
      );
      if (error) {
        setRemoveBackgroundError(error?.code || 'error');
      } else {
        setTempBGRemovedImageBase64(tempBase64);
      }
      setIsRemovingBackground(false);
    };

    if (open) {
      setRemoveBackgroundError(null);
      setIsRemovingBackground(true);
      removeBackground();
    }
  }, [originalImageSrc, open]);

  return (
    <BackgroundRemovalModalBase
      open={open}
      onClose={onClose}
      onModalSubmit={() => onCoverPhotoBackgroundRemovalModalSubmit(tempBGRemovedImageBase64)}
      imageSrc={
        tempBGRemovedImageBase64
          ? `data:image/jpeg;base64,${tempBGRemovedImageBase64}`
          : originalImageSrc
      }
      isUploading={isUploading}
      hasUploadError={hasUploadError}
      isRemovingBackground={isRemovingBackground}
      removeBackgroundError={removeBackgroundError}
      helperText="Photo distorted? Consider retaking this photo against a plain background."
    />
  );
};

export default CoverPhotoBackgroundRemovalModal;
